import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/auth-js';

import { AuthContext, type AuthApi } from './auth-context';

export function AuthProvider({ auth, children }: { auth: AuthApi; children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [recovering, setRecovering] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void auth.getSession().then(({ data }) => {
      if (!cancelled) {
        setSession((current) => current ?? data.session);
        setInitializing(false);
      }
    });

    const {
      data: { subscription },
    } = auth.onAuthStateChange((event, nextSession) => {
      if (cancelled) {
        return;
      }
      setSession(nextSession);
      setInitializing(false);
      if (event === 'PASSWORD_RECOVERY') {
        setRecovering(true);
      }
      if (event === 'USER_UPDATED' || event === 'SIGNED_OUT') {
        setRecovering(false);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [auth]);

  const state = useMemo(
    () => ({ session, initializing, recovering, auth }),
    [session, initializing, recovering, auth],
  );

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}
